import type { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';

import type { AppState } from 'bootstrap.store';

import { hasFeature } from '../../../components/shared/featureFlag';

import { legacyTwilioMiddleware } from './middleware.legacy';
import { voiceSdkTwilioMiddleware } from './voiceSdk/middleware';

// Typed as `any` because the legacy middleware writes an untyped connection
// object here. Tighten to `Call | null` once the legacy path is removed.
declare global {
  interface Window {
    currentPhoneCall: any;
  }
}

window.currentPhoneCall = null;

/**
 * Wrapper that instantiates both the legacy (twilio-client) and the cleaned-up
 * (@twilio/voice-sdk) middlewares, but only forwards TWILIO actions to one of
 * them based on the `use-cleaned-up-telephony-middleware` feature flag.
 */
export const twilioMiddleware =
  (): Middleware<unknown, AppState> =>
  (store: MiddlewareAPI<Dispatch<AnyAction>, AppState>) => {
    const legacyInner = legacyTwilioMiddleware()(store);
    const voiceSdkInner = voiceSdkTwilioMiddleware()(store);

    return (next: Dispatch<AnyAction>) =>
      (action: AnyAction): AnyAction => {
        const isTwilioAction =
          action.type && action.type.indexOf('TWILIO') > -1;

        if (!isTwilioAction) {
          return next(action);
        }

        const useCleanedUp = hasFeature(
          store.getState(),
          'use-cleaned-up-telephony-middleware',
        );

        if (useCleanedUp) {
          return voiceSdkInner(next)(action);
        }

        return legacyInner(next)(action);
      };
  };
